import React from "react";
import { Drawer, Tag } from "antd";
import { Product } from "../../../Types";
import { drawerTitle } from "../../../components";

interface StockHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
  product: Product | null;
}

export const StockHistoryDrawer: React.FC<StockHistoryDrawerProps> = ({
  open,
  onClose,
  product,
}) => {
  if (!product) return null;

  return (
    <Drawer
      title={drawerTitle(
        "Stock Change History",
        `${product.title} (SKU: ${product.sku_code})`,
      )}
      open={open}
      onClose={onClose}
      width={420}
    >
      {!product.stock_history || product.stock_history.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <span className="text-sm">No stock adjustments logged yet.</span>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {product.stock_history.map((hist: any, hIdx: number) => {
            const isPositive = hist.adjusted_quantity > 0;
            const badgeColor = isPositive
              ? "success"
              : hist.adjusted_quantity < 0
                ? "error"
                : "default";
            const changeSymbol = isPositive ? "+" : "";

            let dateStr = "";
            try {
              dateStr = new Date(hist.created_at).toLocaleString("en-US", {
                year: "numeric",
                month: "short",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              });
            } catch {
              dateStr = hist.created_at;
            }

            return (
              <div
                key={hist.id || hIdx}
                className="p-3.5 bg-gray-50 border border-gray-100 rounded-lg flex flex-col gap-2 hover:bg-gray-100/50 transition-colors"
              >
                <div className="flex items-center justify-between gap-2">
                  <Tag
                    color={
                      hist.action_type === "Initial Stock"
                        ? "blue"
                        : hist.action_type === "Bulk Import"
                          ? "purple"
                          : hist.action_type === "Stock Removed"
                            ? "red"
                            : "orange"
                    }
                    className="text-xxs m-0 uppercase font-semibold border-none px-1.5 py-0.5"
                  >
                    {hist.action_type}
                  </Tag>
                  <span
                    className="text-gray-400 font-medium"
                    style={{ fontSize: "10px" }}
                  >
                    {dateStr}
                  </span>
                </div>
                <div className="flex items-start justify-between gap-3 mt-0.5">
                  <p className="text-xs text-gray-600 font-normal leading-relaxed m-0 pr-1 flex-1">
                    {hist.note}
                  </p>
                  <Tag
                    color={badgeColor}
                    className="font-bold font-mono text-xs m-0 px-2 py-0.5 border-none rounded"
                  >
                    {changeSymbol}
                    {hist.adjusted_quantity}
                  </Tag>
                </div>
                <div className="text-xxs text-gray-400 mt-0.5 flex items-center gap-1">
                  <span>Action by:</span>
                  <span className="font-semibold text-gray-600">
                    {hist.changed_by_username || "System"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Drawer>
  );
};
